import React, { useCallback, useEffect } from 'react';
import { View } from 'react-native';
import { observer } from 'mobx-react-lite';
import { PREVIEW_PRODUCT_TYPES } from 'src/store/_models/preview.constants';
import { ProductPreviewProps } from '@photosi/rn-components/product-preview/ProductPreview.types';
import { useStore } from 'src/contexts/StoreContext';
import ResponsiveScaleProvider from 'src/contexts/ResponsiveScaleProvider';
import { useSharedValue } from 'react-native-reanimated';
import { ContainerPreviewV3 } from './ContainerPreviewV3';
import { UserTextModelInstance } from 'src/store/_models/userText';
import { useResponsiveScale } from 'src/contexts/ResponsiveScaleContext';
import { useGridColumn } from 'src/contexts/GridColumnContext';
import FullscreenPreview from '@photosi/rn-components/product-preview/fullscreen-preview/FullscreenPreview';
import { Preview } from '@photosi/rn-components/product-preview/preview/Preview';

export const ProductPreviewV3 = observer(
  ({
    productType = PREVIEW_PRODUCT_TYPES.FRAME,
    preview,
    editable = false,
    swappable = false,
    showGrid = false,
    shownFullScreen = false,
    disabled = false,
    disableSwapImageAnimation = false,
    renderOverlays,
    shouldUseContainerDimensions = false,
    showOverlays = false,
    isHighlighted = false,
    showBadges = true,
    isDropdownOpen = false,
    userTexts = [],
  }: ProductPreviewProps) => {
    const {
      project: { activeUserImageArea },
    } = useStore();

    const previewViewBox = {
      width: preview.viewBox.width,
      height: preview.viewBox.height,
    };
    const activeUserImageAreaViewBox = {
      width: activeUserImageArea?.width as number,
      height: activeUserImageArea?.height as number,
    };

    const isFullScreenPreview = shownFullScreen && activeUserImageArea;
    const containerViewBox = isFullScreenPreview ? previewViewBox : undefined;
    const viewBox = isFullScreenPreview ? activeUserImageAreaViewBox : previewViewBox;

    // State for direct integration mode with SharedValue
    const containerDimensionsSV = useSharedValue<{ width: number; height: number }>({
      width: 0,
      height: 0,
    });

    const gridColumn = useGridColumn();
    useEffect(() => {
      if (shouldUseContainerDimensions) {
        containerDimensionsSV.value = { width: 0, height: 0 };
      }
    }, [gridColumn]);

    // Callback to receive SharedValue from ContainerPreview
    const handleContainerDimensionsChange = useCallback((dimensions: { width: number; height: number }) => {
      containerDimensionsSV.value = dimensions;
    }, []);

    return (
      <>
        <ContainerPreviewV3
          shouldUseContainerDimensions={shouldUseContainerDimensions}
          onContainerDimensionsChange={handleContainerDimensionsChange}
        >
          <ResponsiveScaleProvider
            containerViewBox={containerViewBox}
            viewBox={viewBox}
            containerDimensionsSV={shouldUseContainerDimensions ? containerDimensionsSV : undefined}
          >
            <StablePreviewRenderer
              isFullScreenPreview={!!isFullScreenPreview}
              preview={preview}
              disabled={disabled}
              editable={editable}
              showGrid={showGrid}
              disableSwapImageAnimation={disableSwapImageAnimation}
              productType={productType}
              swappable={swappable}
              renderOverlays={renderOverlays}
              showOverlays={showOverlays}
              isHighlighted={isHighlighted}
              showBadges={showBadges}
              isDropdownOpen={isDropdownOpen}
              userTexts={userTexts}
            />
          </ResponsiveScaleProvider>
        </ContainerPreviewV3>
      </>
    );
  },
);

// Internal component that handles stable rendering based on scale readiness
const StablePreviewRenderer = observer(
  ({
    isFullScreenPreview,
    preview,
    disabled,
    editable,
    showGrid,
    disableSwapImageAnimation,
    productType,
    swappable,
    renderOverlays,
    showOverlays,
    isHighlighted,
    showBadges,
    userTexts,
  }: {
    isFullScreenPreview: boolean;
    preview: any; // TODO: sistemare i tipi
    disabled: boolean;
    editable: boolean;
    showGrid: boolean;
    disableSwapImageAnimation: boolean;
    productType: string;
    swappable: boolean;
    renderOverlays?: any;
    showOverlays: boolean;
    isHighlighted: any;
    showBadges: boolean;
    userTexts: UserTextModelInstance[];
  }) => {
    const { responsiveScale } = useResponsiveScale();

    // Wait for scale to be ready before rendering to prevent flash
    if (responsiveScale <= 0) {
      return <View style={{ opacity: 0, width: '100%', height: '100%' }} accessible={false} />;
    }

    // Render actual content when scale is stable
    return isFullScreenPreview ? (
      <FullscreenPreview
        preview={preview}
        disabled={disabled}
        editable={editable}
        showGrid={showGrid}
        disableSwapImageAnimation={disableSwapImageAnimation}
      />
    ) : (
      <Preview
        productType={productType}
        editable={editable}
        showGrid={showGrid}
        swappable={swappable}
        disabled={disabled}
        preview={preview}
        disableSwapImageAnimation={disableSwapImageAnimation}
        renderOverlays={renderOverlays}
        showOverlays={showOverlays}
        isHighlighted={isHighlighted}
        showBadges={showBadges}
        userTexts={userTexts}
      />
    );
  },
);
